/* eslint-disable @typescript-eslint/no-explicit-any */
import Screen from "@/src/components/Screen";
import { Stepper } from "@mantine/core";
import { useEffect, useState } from "react";
import Step1 from "./Form/Step1";
import { textVariants } from "@/src/components/Text/Text";
import apiRoutes from "@/src/apiRoutes";
import api from "@/src/utils";
import { P2PComponentsData } from "../../P2PTrade/types";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { useForm } from "react-hook-form";
import Step2 from "./Form/Step2";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckEnablers } from "@/src/hooks/check-enablers";
import { Modal } from "@/src/components/Modal/Modal";
import verifyImg from "@assets/lockandkey.svg";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";

const createState = z.object({
  amount: z.number(),
  price_type: z.string(),
  coin: z.number(),
  rate: z.string().nonempty({ message: "This field is required" }),
  trans_type: z.string(),
  currency: z.string(),
  payment_methods: z.number().array(),
  terms: z.string(),
  max_order_price: z.string().nonempty({ message: "This field is required" }),
  min_order_price: z.string().nonempty({ message: "This field is required" }),
  offer_time_limit: z.number(),
});

export type createType = z.infer<typeof createState>;
const Create = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.p2p.create.${key}`);
  const { data: kycData, isLoading } = CheckEnablers("kyc");
  const navigate = useNavigate();

  const validSteps = [
    {
      id: "1",
      fields: [
        "min_order_price",
        "max_order_price",
        "price_type",
        "coin",
        "currency",
        "rate",
        "amount",
      ],
    },
    {
      id: "2",
      fields: ["terms", "payment_methods"],
    },
  ];

  const [active, setActive] = useState(0);
  const fetchP2pComponents = async () => {
    const res = await api.post(
      createForm.watch("currency")
        ? `${apiRoutes.fetchP2pComponents}${createForm
            .watch("currency")
            .slice(0, 3)}/`
        : apiRoutes.fetchP2pComponents,
      createForm.watch("coin") && {
        coin: createForm.watch("coin"),
      }
    );
    return res.data;
  };
  const createForm = useForm<createType>({
    defaultValues: {
      trans_type: "buy",
      offer_time_limit: 30,
    },
    resolver: zodResolver(createState),
  });

  const { data } = useQuery<P2PComponentsData>({
    queryKey: [
      "fetch_p2p_components",
      createForm.watch("currency"),
      createForm.watch("coin"),
    ],
    queryFn: fetchP2pComponents,
  });

  const next = async () => {
    const fields = validSteps[active].fields;
    const output = await createForm.trigger(fields as any, {
      shouldFocus: true,
    });
    if (!output) return;
    setActive((prev) => prev + 1);
  };

  const back = () => {
    setActive((prev) => prev - 1);
  };

  useEffect(() => {
    if (
      data &&
      (isEmpty(createForm.watch("coin")?.toString()) ||
        isEmpty(createForm.watch("currency")))
    ) {
      createForm.setValue("coin", data?.info.coins[0].id);
      createForm.setValue(
        "currency",
        data?.info.currencies[0].currency +
          data?.info?.currencies[0].id.toString()
      );
      createForm.setValue(
        "price_type",
        data?.info.price_types[0].split(" ")[0]
      );
      // setValue("currency", data?.info.currencies[0].currency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Screen loading={isLoading}>
      <div className="md:pb-4">
        <span className={textVariants({ size: "xxl", font: "semibold" })}>
          {trans("header")}
        </span>
      </div>
      <div className="md:px-10 px-2 py-5">
        <Stepper
          size="xs"
          color="#2F60E0"
          active={active}
          onStepClick={setActive}
          allowNextStepsSelect={false}
        >
          <Stepper.Step>
            <Step1 next={next} data={data} form={createForm} />
          </Stepper.Step>
          <Stepper.Step>
            <Step2
              back={back}
              data={data}
              form={createForm}
              setActive={setActive}
            />
          </Stepper.Step>
          {/* <Stepper.Step>
            <Step3 />
          </Stepper.Step> */}
        </Stepper>
      </div>
      <Modal
        title={trans("verify.title")}
        open={kycData?.info !== "Verified"}
        setOpen={console.log}
      >
        <div>
          <div>
            <img src={verifyImg} alt="verify" className="h-40 w-full" />
          </div>
          <div className="text-center">
            <span className={textVariants({ variant: "desc" })}>
              {trans("verify.description")}
            </span>
          </div>
          <div className="bg-alt-bgc rounded-lg px-5 py-3 flex items-center justify-between">
            <span className={textVariants({ size: "lg" })}>
              {trans("verify.kycStatus")}
            </span>
            <span className={textVariants({ size: "sm", case: "capitalize" })}>
              {kycData?.info}
            </span>
          </div>
          <div className="flex items-center justify-center mt-5">
            <span
              onClick={() => navigate(-1)}
              className={cn(
                textVariants({ variant: "link" }),
                "cursor-pointer"
              )}
            >
              {trans("verify.buttons.goBack")}
            </span>
          </div>
        </div>
      </Modal>
    </Screen>
  );
};

export default Create;
